// src/components/header/Header.js
import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref as dbRef,
  update as dbUpdate,
  remove,
  push,
} from "firebase/database";
import { uploadFile } from "../../config/uploadFile";
import { app } from "../../config/firebaseConfig";
import { useData } from "../../context/DataContext";
import ImageUploadField from "../../utils/ImageUploadField";
import { confirmDialog, confirmDelete, successToast, errorToast } from "../../utils/dialog";

const db = getDatabase(app);

const HeaderAdmin = () => {
  const {
    headerLinks = [],
    headerConfig = {},
    addHeaderLink,
  } = useData() || {};

  const [localHeaderConfig, setLocalHeaderConfig] = useState({
    logoUrl: "",
    darkLogoUrl: "",
    menuIconUrl: "",
    closeIconUrl: "",
    stickyBgColor: "",
    stickyTextColor: "",
  });
  const [localFiles, setLocalFiles] = useState({});
  const [localHeaderLinks, setLocalHeaderLinks] = useState([]);
  const [newLink, setNewLink] = useState({ label: "", path: "", order: 0 });

  // Inline editing
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLinkData, setEditingLinkData] = useState({ label: "", path: "", order: 0 });

  // Sublinks panel state: which link's sublinks panel is open
  const [expandedSubId, setExpandedSubId] = useState(null);
  // New sublink form per link
  const [newSublink, setNewSublink] = useState({ label: "", path: "", order: 0 });

  // Inline sublink editing
  const [editingSublinkId, setEditingSublinkId] = useState(null); // "parentId|subId"
  const [editingSublinkData, setEditingSublinkData] = useState({ label: "", path: "", order: 0 });

  useEffect(() => {
    setLocalHeaderConfig({
      logoUrl: headerConfig.logoUrl || "",
      darkLogoUrl: headerConfig.darkLogoUrl || "",
      menuIconUrl: headerConfig.menuIconUrl || "",
      closeIconUrl: headerConfig.closeIconUrl || "",
      stickyBgColor: headerConfig.stickyBgColor || "",
      stickyTextColor: headerConfig.stickyTextColor || "",
    });
    setLocalHeaderLinks(headerLinks);
    // Auto-expand any link that already has sublinks
    const withSubs = headerLinks.find(
      (l) => l.sublinks && Object.keys(l.sublinks).length > 0
    );
    if (withSubs) setExpandedSubId((prev) => prev ?? withSubs.id);
  }, [headerConfig, headerLinks]);

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setLocalFiles((files) => ({ ...files, [field]: file }));
    const previewUrl = URL.createObjectURL(file);
    setLocalHeaderConfig((cfg) => ({ ...cfg, [field]: previewUrl }));
  };

  const handleLinkChange = (e) => {
    const { name, value } = e.target;
    setNewLink((old) => ({ ...old, [name]: name === "order" ? Number(value) : value }));
  };

  const handleAddLink = (e) => {
    e.preventDefault();
    if (!newLink.label || !newLink.path) return;
    const id = Date.now().toString();
    setLocalHeaderLinks((list) => [...list, { id, ...newLink, sublinks: {} }]);
    setNewLink({ label: "", path: "", order: 0 });
    successToast("Link added to queue.");
  };

  const handleStartEdit = (link) => {
    setEditingLinkId(link.id);
    setEditingLinkData({ label: link.label, path: link.path, order: link.order });
  };

  const handleSaveEdit = (id) => {
    if (!editingLinkData.label || !editingLinkData.path) return;
    setLocalHeaderLinks((list) =>
      list.map((l) => (l.id === id ? { ...l, ...editingLinkData } : l))
    );
    setEditingLinkId(null);
    successToast("Link updated locally.");
  };

  const handleCancelEdit = () => setEditingLinkId(null);

  const handleDeleteLink = async (id) => {
    if (!(await confirmDelete({ title: "Remove link?", text: "Remove this link?", confirmText: "Yes, remove" })))
      return;
    setLocalHeaderLinks((list) => list.filter((l) => l.id !== id));
    successToast("Link removed.");
  };

  // ---------- Sublink handlers ----------
  const getSublinks = (link) => {
    const s = link.sublinks || {};
    return Object.entries(s).map(([id, v]) => ({ id, ...v })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  const handleAddSublink = async (parentId) => {
    if (!newSublink.label || !newSublink.path) return;
    try {
      const ref = push(dbRef(db, `header/header1/links/${parentId}/sublinks`), newSublink);
      const subId = ref.key;
      setLocalHeaderLinks((list) =>
        list.map((l) =>
          l.id === parentId
            ? { ...l, sublinks: { ...(l.sublinks || {}), [subId]: { ...newSublink } } }
            : l
        )
      );
      setNewSublink({ label: "", path: "", order: 0 });
      successToast("Sublink added!");
    } catch (err) {
      errorToast("Failed to add sublink.");
    }
  };

  const handleStartEditSublink = (parentId, sub) => {
    setEditingSublinkId(`${parentId}|${sub.id}`);
    setEditingSublinkData({ label: sub.label, path: sub.path, order: sub.order ?? 0 });
  };

  const handleSaveEditSublink = async (parentId, subId) => {
    if (!editingSublinkData.label || !editingSublinkData.path) return;
    try {
      await dbUpdate(dbRef(db, `header/header1/links/${parentId}/sublinks/${subId}`), editingSublinkData);
      setLocalHeaderLinks((list) =>
        list.map((l) => {
          if (l.id !== parentId) return l;
          return {
            ...l,
            sublinks: {
              ...(l.sublinks || {}),
              [subId]: { ...(l.sublinks?.[subId] || {}), ...editingSublinkData },
            },
          };
        })
      );
      setEditingSublinkId(null);
      successToast("Sublink updated!");
    } catch (err) {
      errorToast("Failed to update sublink.");
    }
  };

  const handleCancelEditSublink = () => setEditingSublinkId(null);

  const handleDeleteSublink = async (parentId, subId) => {
    if (!(await confirmDelete({ title: "Remove sublink?", text: "Remove this dropdown item?", confirmText: "Yes" })))
      return;
    try {
      await remove(dbRef(db, `header/header1/links/${parentId}/sublinks/${subId}`));
      setLocalHeaderLinks((list) =>
        list.map((l) => {
          if (l.id !== parentId) return l;
          const s = { ...(l.sublinks || {}) };
          delete s[subId];
          return { ...l, sublinks: s };
        })
      );
      successToast("Sublink removed.");
    } catch (err) {
      errorToast("Failed to remove sublink.");
    }
  };

  // ---------- Save all ----------
  const handleSave = async () => {
    if (!(await confirmDialog({ title: "Save changes?", text: "Save all header configuration changes?", confirmText: "Yes, save" })))
      return;
    try {
      const updates = {
        stickyBgColor: localHeaderConfig.stickyBgColor || "",
        stickyTextColor: localHeaderConfig.stickyTextColor || "",
      };
      for (const field of ["logoUrl", "darkLogoUrl", "menuIconUrl"]) {
        if (localFiles[field]) {
          const url = await uploadFile(localFiles[field]);
          updates[field] = url;
        } else if (localHeaderConfig[field] === "") {
          updates[field] = "";
        }
      }
      if (Object.keys(updates).length > 0) {
        await dbUpdate(dbRef(db, "header/header1"), updates);
      }
      // Save links (preserving sublinks already saved live)
      await remove(dbRef(db, "header/header1/links"));
      for (const link of localHeaderLinks) {
        const linkRef = await push(dbRef(db, "header/header1/links"), {
          label: link.label,
          path: link.path,
          order: link.order,
        });
        // re-save any sublinks under the new key
        const subs = link.sublinks || {};
        for (const [, sub] of Object.entries(subs)) {
          await push(dbRef(db, `header/header1/links/${linkRef.key}/sublinks`), {
            label: sub.label,
            path: sub.path,
            order: sub.order ?? 0,
          });
        }
      }
      successToast("Header configuration saved successfully.");
    } catch (err) {
      console.error("Save failed:", err);
      errorToast("Error saving changes. Check console.");
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* Left Column: Asset Images */}
        <div className="xl:col-span-1 space-y-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-fit">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 text-white">
              <h3 className="font-bold text-lg">Header Images (Assets)</h3>
              <p className="text-gray-300 text-xs mt-0.5">Upload brand logo, hamburger toggle, and close state icons.</p>
            </div>
            <div className="p-6 space-y-6">
              {[
                { field: "logoUrl", label: "Logo Asset (Light/Gold)", hint: "For transparent dark headers. Recommended: 200×200px (Light PNG/SVG)" },
                { field: "darkLogoUrl", label: "Logo Asset (Dark/Forest Green)", hint: "For sticky white headers. Recommended: 200×200px (Dark PNG/SVG)" },
                { field: "menuIconUrl", label: "Hamburger Menu Icon", hint: "Recommended: 64×64px (Square PNG)" },
              ].map(({ field, label, hint }) => (
                <ImageUploadField
                  key={field}
                  label={label}
                  hint={hint}
                  aspect="square"
                  previewUrl={localHeaderConfig[field]}
                  file={localFiles[field]}
                  onChange={(e) => handleFileChange(e, field)}
                  onClear={
                    field === "logoUrl"
                      ? undefined
                      : () => {
                          setLocalFiles((files) => { const copy = { ...files }; delete copy[field]; return copy; });
                          setLocalHeaderConfig((cfg) => ({ ...cfg, [field]: "" }));
                        }
                  }
                />
              ))}

              {/* Sticky Header Styling */}
              <div className="border-t border-gray-100 pt-6 space-y-4">
                <h4 className="font-bold text-sm text-gray-700">Sticky Header Aesthetics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500">Background Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={localHeaderConfig.stickyBgColor || "#ffffff"}
                        onChange={(e) => setLocalHeaderConfig(cfg => ({ ...cfg, stickyBgColor: e.target.value }))}
                        className="w-10 min-w-[2.5rem] h-10 border border-gray-300 rounded cursor-pointer p-0"
                      />
                      <input
                        type="text"
                        value={localHeaderConfig.stickyBgColor || ""}
                        onChange={(e) => setLocalHeaderConfig(cfg => ({ ...cfg, stickyBgColor: e.target.value }))}
                        placeholder="#ffffff"
                        className="flex-1 border border-gray-300 rounded px-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500">Menu Text Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={localHeaderConfig.stickyTextColor || "#14251F"}
                        onChange={(e) => setLocalHeaderConfig(cfg => ({ ...cfg, stickyTextColor: e.target.value }))}
                        className="w-10 min-w-[2.5rem] h-10 border border-gray-300 rounded cursor-pointer p-0"
                      />
                      <input
                        type="text"
                        value={localHeaderConfig.stickyTextColor || ""}
                        onChange={(e) => setLocalHeaderConfig(cfg => ({ ...cfg, stickyTextColor: e.target.value }))}
                        placeholder="#14251F"
                        className="flex-1 border border-gray-300 rounded px-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setLocalHeaderConfig(cfg => ({ ...cfg, stickyBgColor: "#ffffff", stickyTextColor: "#14251F" }))}
                    className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
                  >
                    Reset to Defaults
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Links Management */}
        <div className="xl:col-span-2 space-y-8">

          {/* Add Link Form */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 text-white">
              <h3 className="font-bold text-lg">Manage Header Links</h3>
              <p className="text-gray-300 text-xs mt-0.5">Control order, labels, and redirect routes for the navbar. Add sublinks to create dropdown menus.</p>
            </div>
            <div className="p-6 space-y-6">
              <form onSubmit={handleAddLink} className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Link Label</label>
                  <input name="label" value={newLink.label} onChange={handleLinkChange} placeholder="e.g. Our Villas" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Relative Path</label>
                  <input name="path" value={newLink.path} onChange={handleLinkChange} placeholder="e.g. /gallery" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sort Order</label>
                  <input name="order" type="number" value={newLink.order} onChange={handleLinkChange} placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
                </div>
                <div className="flex items-end">
                  <button type="submit" className="w-full bg-[#14251F] hover:bg-[#1b332b] text-[#FFD1A0] font-bold rounded-lg px-4 py-2.5 transition text-xs uppercase tracking-wider shadow-md cursor-pointer">
                    Add Link
                  </button>
                </div>
              </form>

              {/* Links Table */}
              <div className="space-y-3">
                {localHeaderLinks
                  .sort((a, b) => a.order - b.order)
                  .map((link) => {
                    const isEditing = editingLinkId === link.id;
                    const isExpanded = expandedSubId === link.id;
                    const sublinks = getSublinks(link);

                    return (
                      <div key={link.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Link row */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                          {/* Order */}
                          <div className="w-14 shrink-0">
                            {isEditing ? (
                              <input
                                type="number"
                                value={editingLinkData.order}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, order: Number(e.target.value) })}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs outline-none"
                              />
                            ) : (
                              <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold border border-gray-200">{link.order}</span>
                            )}
                          </div>

                          {/* Label */}
                          <div className="flex-1 min-w-0">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingLinkData.label}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, label: e.target.value })}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none font-bold"
                              />
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-800 text-sm">{link.label}</span>
                                {sublinks.length > 0 && (
                                  <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                    {sublinks.length} sublink{sublinks.length > 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Path */}
                          <div className="w-36 shrink-0">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingLinkData.path}
                                onChange={(e) => setEditingLinkData({ ...editingLinkData, path: e.target.value })}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs outline-none font-mono"
                              />
                            ) : (
                              <span className="text-gray-500 font-mono text-xs">{link.path}</span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex gap-1.5 shrink-0">
                            {isEditing ? (
                              <>
                                <button onClick={() => handleSaveEdit(link.id)} className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold cursor-pointer">Save</button>
                                <button onClick={handleCancelEdit} className="px-2.5 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs font-bold cursor-pointer">Cancel</button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setExpandedSubId(isExpanded ? null : link.id)}
                                  className={`px-2.5 py-1 rounded text-xs font-bold cursor-pointer transition ${
                                    isExpanded ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                  }`}
                                  title="Manage dropdown sublinks"
                                >
                                  ⌄ Sublinks
                                </button>
                                <button onClick={() => handleStartEdit(link)} className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-bold cursor-pointer">Edit</button>
                                <button onClick={() => handleDeleteLink(link.id)} className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold cursor-pointer">Delete</button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Sublinks panel */}
                        {isExpanded && (
                          <div className="border-t border-indigo-100 bg-indigo-50 px-4 py-4 space-y-3">
                            <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
                              Dropdown Sublinks for "{link.label}"
                            </p>

                            {/* Existing sublinks */}
                            {sublinks.length > 0 ? (
                              <div className="space-y-2">
                                {sublinks.map((sub) => {
                                  const editKey = `${link.id}|${sub.id}`;
                                  const isEditingSub = editingSublinkId === editKey;
                                  return (
                                    <div key={sub.id} className="flex items-center gap-2 bg-white border border-indigo-200 rounded-lg px-3 py-2">
                                      {isEditingSub ? (
                                        <>
                                          <input
                                            type="number"
                                            value={editingSublinkData.order}
                                            onChange={(e) => setEditingSublinkData({ ...editingSublinkData, order: Number(e.target.value) })}
                                            className="w-12 border border-indigo-300 rounded px-1.5 py-1 text-xs outline-none text-center"
                                          />
                                          <input
                                            type="text"
                                            value={editingSublinkData.label}
                                            onChange={(e) => setEditingSublinkData({ ...editingSublinkData, label: e.target.value })}
                                            className="flex-1 border border-indigo-300 rounded px-2 py-1 text-sm outline-none font-medium"
                                            placeholder="Label"
                                          />
                                          <input
                                            type="text"
                                            value={editingSublinkData.path}
                                            onChange={(e) => setEditingSublinkData({ ...editingSublinkData, path: e.target.value })}
                                            className="w-36 border border-indigo-300 rounded px-2 py-1 text-xs outline-none font-mono"
                                            placeholder="/path"
                                          />
                                          <button onClick={() => handleSaveEditSublink(link.id, sub.id)} className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold cursor-pointer">Save</button>
                                          <button onClick={handleCancelEditSublink} className="px-2.5 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs font-bold cursor-pointer">Cancel</button>
                                        </>
                                      ) : (
                                        <>
                                          <span className="bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded font-bold w-8 text-center shrink-0">{sub.order ?? 0}</span>
                                          <span className="font-medium text-gray-800 text-sm flex-1">{sub.label}</span>
                                          <span className="text-gray-400 font-mono text-xs">{sub.path}</span>
                                          <button onClick={() => handleStartEditSublink(link.id, sub)} className="px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded text-xs font-bold cursor-pointer">Edit</button>
                                          <button onClick={() => handleDeleteSublink(link.id, sub.id)} className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-bold cursor-pointer">✕</button>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-indigo-400 italic">No sublinks yet. Add one below to create a dropdown menu.</p>
                            )}

                            {/* Add sublink form */}
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1">
                              <input
                                value={newSublink.label}
                                onChange={(e) => setNewSublink((s) => ({ ...s, label: e.target.value }))}
                                placeholder="Label (e.g. Vivaak Villa)"
                                className="sm:col-span-2 border border-indigo-300 rounded-lg px-3 py-2 text-sm outline-none bg-white focus:ring-2 focus:ring-indigo-400"
                              />
                              <input
                                value={newSublink.path}
                                onChange={(e) => setNewSublink((s) => ({ ...s, path: e.target.value }))}
                                placeholder="Path (e.g. /dharampur-villa)"
                                className="border border-indigo-300 rounded-lg px-3 py-2 text-sm outline-none bg-white focus:ring-2 focus:ring-indigo-400 font-mono"
                              />
                              <button
                                onClick={() => handleAddSublink(link.id)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg px-3 py-2 text-xs uppercase tracking-wider cursor-pointer transition"
                              >
                                + Add Sublink
                              </button>
                            </div>
                            <p className="text-xs text-indigo-400">💡 Sublinks are saved immediately to Firebase. Use "Save Header Changes" to re-save top-level link order.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Header Preview */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 text-white">
              <h3 className="font-bold text-lg">Visual Navigation Bar Preview</h3>
              <p className="text-gray-300 text-xs mt-0.5">Observe real-time preview of changes before locking them live.</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Transparent preview */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Transparent Header (Dark Background)</h4>
                <div className="flex items-center justify-between p-6 bg-[#14251F] rounded-xl shadow-inner border border-white/5">
                  {localHeaderConfig.logoUrl ? (
                    <img src={localHeaderConfig.logoUrl} alt="Logo Preview" className="h-10 object-contain" />
                  ) : (
                    <span className="text-white font-bold tracking-wider text-xs">VIVAAK</span>
                  )}
                  <nav className="hidden md:flex space-x-6 text-xs text-[#FFD1A0] font-bold tracking-widest uppercase">
                    {localHeaderLinks.sort((a, b) => a.order - b.order).map((link) => (
                      <span key={link.id} className="cursor-pointer hover:underline flex items-center gap-1">
                        {link.label}
                        {getSublinks(link).length > 0 && <span className="text-white/50 text-[10px]">▾</span>}
                      </span>
                    ))}
                  </nav>
                  <div className="flex space-x-4">
                    {localHeaderConfig.menuIconUrl && <img src={localHeaderConfig.menuIconUrl} alt="Menu" className="h-5 object-contain invert" />}
                  </div>
                </div>
              </div>

              {/* Sticky preview */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sticky Header (Light Background)</h4>
                <div className="flex items-center justify-between p-6 bg-white rounded-xl shadow-inner border border-gray-200">
                  {localHeaderConfig.darkLogoUrl || localHeaderConfig.logoUrl ? (
                    <img src={localHeaderConfig.darkLogoUrl || localHeaderConfig.logoUrl} alt="Dark Logo" className="h-10 object-contain" />
                  ) : (
                    <span className="text-gray-900 font-bold tracking-wider text-xs">VIVAAK</span>
                  )}
                  <nav className="hidden md:flex space-x-6 text-xs text-[#14251F] font-bold tracking-widest uppercase">
                    {localHeaderLinks.sort((a, b) => a.order - b.order).map((link) => (
                      <span key={link.id} className="cursor-pointer hover:underline flex items-center gap-1">
                        {link.label}
                        {getSublinks(link).length > 0 && <span className="text-gray-400 text-[10px]">▾</span>}
                      </span>
                    ))}
                  </nav>
                  <div className="flex space-x-4">
                    {localHeaderConfig.menuIconUrl && <img src={localHeaderConfig.menuIconUrl} alt="Menu" className="h-5 object-contain" />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Bar */}
      <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 flex items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          💡 Sublinks are saved live. Click "Save Header Changes" to update logo assets and link order.
        </p>
        <button
          onClick={handleSave}
          className="px-6 py-3 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] font-bold text-xs uppercase tracking-wider rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer"
        >
          📁 Save Header Changes
        </button>
      </div>
    </div>
  );
};

export default HeaderAdmin;
